import { FiCheck, FiUsers, FiBarChart2, FiRotateCcw } from "react-icons/fi";
import { Button } from "newui/components/Button";
import SkeletonBase from "newui/components/Skeleton";
import type { Meeting } from "../../types/meeting.type";
import { MeetingStatusBanner } from "./MeetingStatusBanner";

interface LandingViewProps {
  isLoading: boolean;
  isReopening?: boolean;
  isRfxLead: boolean;
  meeting: Meeting | undefined;
  onPreviewScores: () => void;
  onReopenMeeting: () => void;
  onResumeMeeting: () => void;
  onScheduleMeeting: () => void;
  onSkipMeeting: () => void;
  onStartMeeting: () => void;
  onViewResults: () => void;
}

export const LandingView: React.FC<LandingViewProps> = ({
  isRfxLead,
  isLoading,
  meeting,
  onScheduleMeeting,
  onStartMeeting,
  onResumeMeeting,
  onPreviewScores,
  onSkipMeeting,
  onViewResults,
  onReopenMeeting,
  isReopening = false,
}) => {
  if (!isRfxLead) {
    return (
      <div data-testid="landing-view-readonly" className="space-y-4">
        {meeting ? (
          <MeetingStatusBanner meeting={meeting} />
        ) : (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-center">
            <p className="text-sm text-gray-500">
              The RFx Lead is coordinating the panel meeting and award decision for this evaluation.
            </p>
          </div>
        )}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-3" data-testid="landing-view-loading">
        <SkeletonBase startColor="lightgray" width="100%" height="120px" className="!rounded-lg" />
        <SkeletonBase startColor="lightgray" width="100%" height="80px" className="!rounded-lg" />
      </div>
    );
  }

  const hasMeeting = meeting != null;
  const status = meeting?.status;

  return (
    <div data-testid="landing-view" className="space-y-6">
      {meeting && <MeetingStatusBanner meeting={meeting} />}

      {/* Scoring progress when meeting in progress */}
      {meeting && status === "in_progress" && meeting.scoring_progress && (
        <div className="flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
          <div className="flex-1">
            <p className="text-sm font-medium text-blue-800">
              Scoring Progress: {meeting.scoring_progress.completed_criteria} / {meeting.scoring_progress.total_criteria} criteria
            </p>
            <div className="mt-1.5 h-2 rounded-full bg-blue-100 overflow-hidden">
              <div
                className="h-full rounded-full bg-blue-500 transition-all duration-300"
                style={{
                  width: meeting.scoring_progress.total_criteria > 0
                    ? `${(meeting.scoring_progress.completed_criteria / meeting.scoring_progress.total_criteria) * 100}%`
                    : "0%",
                }}
              />
            </div>
          </div>
          {meeting.scoring_progress.is_complete && (
            <span className="text-xs font-medium text-green-700 bg-green-100 rounded-full px-2 py-0.5">
              Complete
            </span>
          )}
        </div>
      )}

      <h3 className="text-base font-medium text-gray-900">
        {hasMeeting ? "Panel Meeting Actions" : "Final Report"}
      </h3>

      {!hasMeeting && (
        <p className="text-sm text-gray-500">
          Choose how you would like to finalise this evaluation.
        </p>
      )}

      {/* Cards: only show when no meeting or in draft/invited state */}
      {(!hasMeeting || status === "draft" || status === "invited") && (
        <div className="flex flex-row gap-4">
          {/* Panel Meeting card */}
          <div className="flex-1 flex flex-col">
            <button
              type="button"
              data-testid="card-panel-meeting"
              onClick={onScheduleMeeting}
              className={`
                relative flex flex-col items-center p-6 rounded-lg border-2
                transition-all duration-150 w-full text-center
                ${hasMeeting
                  ? "border-blue-500 bg-blue-50 cursor-pointer hover:border-blue-600"
                  : "border-gray-200 bg-white cursor-pointer hover:border-blue-400 hover:bg-blue-50"
                }
              `}
            >
              {hasMeeting && (
                <div className="absolute top-2 right-2 text-blue-500">
                  <FiCheck className="h-5 w-5" data-testid="card-panel-meeting-check" />
                </div>
              )}
              <div className={`mb-3 ${hasMeeting ? "text-blue-500" : "text-gray-500"}`}>
                <FiUsers className="h-6 w-6" />
              </div>
              <h4 className="text-sm font-semibold text-gray-900 mb-1">Panel Meeting</h4>
              <p className="text-xs text-gray-500">
                {hasMeeting
                  ? "Manage invitations and facilitate the consensus scoring meeting"
                  : "Schedule a meeting with panel members to discuss and reach consensus scores"}
              </p>
            </button>
          </div>

          {/* Preview and Award card */}
          <div className="flex-1 flex flex-col">
            <button
              type="button"
              data-testid="card-preview-scores"
              onClick={onPreviewScores}
              className={`
                relative flex flex-col items-center p-6 rounded-lg border-2
                transition-all duration-150 w-full text-center
                border-gray-200 bg-white cursor-pointer hover:border-blue-400 hover:bg-blue-50
              `}
            >
              <div className="mb-3 text-gray-500">
                <FiBarChart2 className="h-6 w-6" />
              </div>
              <h4 className="text-sm font-semibold text-gray-900 mb-1">Preview and Award</h4>
              <p className="text-xs text-gray-500">
                Review individual panel scores and proceed directly to the award decision
              </p>
            </button>
          </div>
        </div>
      )}

      {/* Status-aware action buttons */}

      {/* Draft: Skip Meeting option */}
      {status === "draft" && (
        <div className="flex gap-3">
          <Button
            btntype="secondary"
            onClick={onSkipMeeting}
            data-testid="skip-meeting-btn"
          >
            Skip Meeting
          </Button>
        </div>
      )}

      {/* Invited: Start Meeting + Manage Invitations + Skip */}
      {status === "invited" && (
        <div className="flex gap-3">
          <Button
            btntype="primary"
            onClick={onStartMeeting}
            data-testid="start-meeting-btn"
          >
            Start Meeting
          </Button>
          <Button
            btntype="secondary"
            onClick={onScheduleMeeting}
            data-testid="manage-invites-btn"
          >
            Manage Invitations
          </Button>
          <Button
            btntype="plain"
            onClick={onSkipMeeting}
            data-testid="skip-meeting-btn"
          >
            Skip Meeting
          </Button>
        </div>
      )}

      {/* In Progress: Resume Meeting */}
      {status === "in_progress" && (
        <div className="flex gap-3">
          <Button
            btntype="primary"
            onClick={onResumeMeeting}
            data-testid="resume-meeting-btn"
          >
            Resume Meeting
          </Button>
          <Button
            btntype="secondary"
            onClick={onViewResults}
            data-testid="view-overall-btn"
          >
            View Overall Scores
          </Button>
        </div>
      )}

      {/* Completed: View Results + Reopen */}
      {status === "completed" && (
        <div className="flex gap-3">
          <Button
            btntype="primary"
            onClick={onViewResults}
            data-testid="view-results-btn"
          >
            View Results & Award
          </Button>
          <Button
            btntype="secondary"
            onClick={onReopenMeeting}
            disabled={isReopening}
            isLoading={isReopening}
            data-testid="reopen-meeting-btn"
          >
            <FiRotateCcw className="h-4 w-4 mr-1.5 inline" />
            Reopen Meeting
          </Button>
        </div>
      )}

      {/* Overridden: View Award */}
      {status === "overridden" && (
        <div className="flex gap-3">
          <Button
            btntype="primary"
            onClick={onPreviewScores}
            data-testid="view-award-btn"
          >
            View Award Decisions
          </Button>
        </div>
      )}
    </div>
  );
};
